import React, { useState } from 'react';
import {
  Activity,
  ChevronDown,
  MessageSquare,
  Calendar,
  XCircle,
  HeartHandshake,
  ShoppingBag,
  CheckCircle2,
  Loader2,
  Clock,
  AlertCircle,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';

interface FlowProgress {
  current_step: number;
  total_steps: number;
  status: 'SCHEDULED' | 'PROCESSING' | 'SUCCESS' | 'FAILED' | 'CANCELED';
  next_scheduled?: string;
}

interface ContactFlowControlProps {
  flowStatus: { [key: string]: any };
  flowProgress?: { [key: string]: any };
  isLoading: boolean;
  onToggle: (flowType: string, isPaused: boolean, reason?: string) => void;
}

const FLOWS: Array<{ key: string; label: string; desc: string }> = [
  { key: 'follow_up', label: 'Follow-up (Conversão)', desc: 'Mensagens para converter leads em agendamentos' },
  { key: 'confirmation', label: 'Confirmações', desc: 'Confirmação de consultas agendadas' },
  { key: 'noshow', label: 'Cobrança de Faltas', desc: 'Mensagens para pacientes que faltaram' },
  { key: 'pos_consulta', label: 'Pós-consulta', desc: 'Follow-up após atendimento' },
  { key: 'pos_venda', label: 'Pós-venda', desc: 'Acompanhamento após venda' },
];

interface FlowVis {
  label: string;
  icon: any;
  iconClassDark: string;
  iconClassLight: string;
  bar: string;
}

const FLOW_VIS: Record<string, FlowVis> = {
  follow_up: {
    label: 'Follow-up',
    icon: MessageSquare,
    iconClassDark: 'bg-sminy-primary/15 text-sminy-primary',
    iconClassLight: 'bg-sminy-primary/10 text-sminy-primary',
    bar: 'from-sminy-primary to-sminy-primary-pressed',
  },
  confirmation: {
    label: 'Confirmação',
    icon: Calendar,
    iconClassDark: 'bg-green-900/40 text-green-300',
    iconClassLight: 'bg-green-50 text-green-600',
    bar: 'from-green-400 to-green-600',
  },
  noshow: {
    label: 'No-show',
    icon: XCircle,
    iconClassDark: 'bg-red-900/40 text-red-300',
    iconClassLight: 'bg-red-50 text-red-600',
    bar: 'from-red-400 to-red-600',
  },
  pos_consulta: {
    label: 'Pós-consulta',
    icon: HeartHandshake,
    iconClassDark: 'bg-purple-900/40 text-purple-300',
    iconClassLight: 'bg-purple-50 text-purple-600',
    bar: 'from-purple-400 to-purple-600',
  },
  pos_venda: {
    label: 'Pós-venda',
    icon: ShoppingBag,
    iconClassDark: 'bg-pink-900/40 text-pink-300',
    iconClassLight: 'bg-pink-50 text-pink-600',
    bar: 'from-pink-400 to-pink-600',
  },
};

const STATUS_LABELS: Record<string, string> = {
  SUCCESS: 'Enviado',
  SCHEDULED: 'Agendado',
  PROCESSING: 'Enviando...',
  FAILED: 'Erro',
};

const formatNextExecution = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays > 0) {
    return `${date.toLocaleDateString('pt-BR')} às ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
  }
  if (diffHours > 0) {
    return `em ${diffHours}h`;
  }
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  return `em ${diffMinutes} min`;
};

const hasActiveFlows = (flowProgress: any): boolean => {
  if (!flowProgress) return false;
  return Object.values(flowProgress).some((flow: any) => flow && flow.status !== 'CANCELED');
};

const ContactFlowControl: React.FC<ContactFlowControlProps> = ({
  flowStatus,
  flowProgress,
  isLoading,
  onToggle,
}) => {
  const { isDark } = useTheme();
  const [showActiveTimeline, setShowActiveTimeline] = useState(true);
  const anyActive = Object.values(flowStatus).some((f: any) => !f.is_paused);

  return (
    <div className="p-4 space-y-4">
      {flowProgress && hasActiveFlows(flowProgress) && (
        <div
          className={`rounded-2xl border overflow-hidden ${
            isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}
        >
          <button
            onClick={() => setShowActiveTimeline((v) => !v)}
            className={`w-full flex items-center gap-3 p-3 transition-colors ${
              isDark ? 'hover:bg-gray-700/40' : 'hover:bg-gray-50'
            }`}
          >
            <div
              className={`p-2 rounded-xl ${
                showActiveTimeline
                  ? 'bg-sminy-primary/20 text-sminy-primary'
                  : isDark
                  ? 'bg-gray-700 text-gray-400'
                  : 'bg-gray-100 text-gray-500'
              }`}
            >
              <Activity className="w-4 h-4" />
            </div>
            <span
              className={`text-sm font-semibold flex-1 text-left ${
                isDark ? 'text-gray-200' : 'text-gray-800'
              }`}
            >
              Fluxos Automatizados Ativos
            </span>
            <ChevronDown
              className={`w-4 h-4 transition-transform duration-200 ${
                showActiveTimeline ? 'rotate-180 text-sminy-primary' : isDark ? 'text-gray-500' : 'text-gray-400'
              }`}
            />
          </button>

          {showActiveTimeline && (
            <div
              className={`p-3 space-y-2 border-t animate-in fade-in slide-in-from-top-2 duration-200 ${
                isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50/50'
              }`}
            >
              {Object.entries(flowProgress).map(([flowType, progress]) => {
                if (!progress || (progress as FlowProgress).status === 'CANCELED') return null;
                const config = FLOW_VIS[flowType];
                if (!config) return null;
                const Icon = config.icon;
                const p = progress as FlowProgress;
                const percent = (p.current_step / p.total_steps) * 100;
                return (
                  <div
                    key={flowType}
                    className={`rounded-xl p-3 border ${
                      isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`p-2 rounded-xl ${
                          isDark ? config.iconClassDark : config.iconClassLight
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <h4
                            className={`font-semibold text-sm truncate ${
                              isDark ? 'text-gray-200' : 'text-gray-900'
                            }`}
                          >
                            {config.label}
                          </h4>
                          <span
                            className={`text-xs flex-shrink-0 ${
                              isDark ? 'text-gray-400' : 'text-gray-500'
                            }`}
                          >
                            Etapa {p.current_step} de {p.total_steps}
                          </span>
                        </div>
                        <div
                          className={`w-full h-2 rounded-full overflow-hidden mb-2 ${
                            isDark ? 'bg-gray-700' : 'bg-gray-200'
                          }`}
                        >
                          <div
                            className={`h-full bg-gradient-to-r ${config.bar} transition-all duration-500`}
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                        <div className="flex items-center gap-3 text-xs flex-wrap">
                          <span
                            className={`flex items-center gap-1 ${
                              p.status === 'SUCCESS'
                                ? isDark ? 'text-green-300' : 'text-green-600'
                                : p.status === 'PROCESSING'
                                ? isDark ? 'text-yellow-300' : 'text-yellow-600'
                                : p.status === 'SCHEDULED'
                                ? 'text-sminy-primary'
                                : isDark ? 'text-red-300' : 'text-red-600'
                            }`}
                          >
                            {p.status === 'SUCCESS' && <CheckCircle2 className="w-3 h-3" />}
                            {p.status === 'PROCESSING' && <Loader2 className="w-3 h-3 animate-spin" />}
                            {p.status === 'SCHEDULED' && <Clock className="w-3 h-3" />}
                            {p.status === 'FAILED' && <AlertCircle className="w-3 h-3" />}
                            {STATUS_LABELS[p.status] || p.status}
                          </span>
                          {p.next_scheduled && p.status === 'SCHEDULED' && (
                            <span
                              className={`flex items-center gap-1 ${
                                isDark ? 'text-gray-400' : 'text-gray-500'
                              }`}
                            >
                              <Calendar className="w-3 h-3" />
                              {formatNextExecution(p.next_scheduled)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div
        className={`rounded-2xl border ${
          isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}
      >
        <div
          className={`px-4 py-3 border-b ${
            isDark ? 'border-gray-700' : 'border-gray-200'
          }`}
        >
          <h3
            className={`text-sm font-semibold ${
              isDark ? 'text-gray-200' : 'text-gray-800'
            }`}
          >
            Controle de Mensagens Automáticas
          </h3>
        </div>
        <div className="p-4">
          {isLoading ? (
            <div className="flex justify-center py-4">
              <div className="w-6 h-6 border-4 border-sminy-primary/30 border-t-sminy-primary rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-3">
              {FLOWS.map(({ key, label, desc }) => {
                const flow = flowStatus[key] || {};
                const isPaused = !!flow.is_paused;
                return (
                  <div key={key} className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-medium truncate ${
                          isDark ? 'text-gray-200' : 'text-gray-800'
                        }`}
                      >
                        {label}
                      </p>
                      <p
                        className={`text-xs truncate ${
                          isDark ? 'text-gray-400' : 'text-gray-500'
                        }`}
                      >
                        {desc}
                      </p>
                    </div>
                    <button
                      onClick={() =>
                        onToggle(
                          key,
                          !isPaused,
                          isPaused ? 'Retomado via chat' : 'Pausado via chat'
                        )
                      }
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                        isPaused
                          ? isDark ? 'bg-gray-700' : 'bg-gray-300'
                          : 'bg-sminy-primary'
                      }`}
                      title={isPaused ? 'Retomar' : 'Pausar'}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                          isPaused ? 'translate-x-1' : 'translate-x-6'
                        }`}
                      />
                    </button>
                  </div>
                );
              })}

              <div
                className={`pt-3 border-t ${
                  isDark ? 'border-gray-700' : 'border-gray-200'
                }`}
              >
                <button
                  onClick={() =>
                    onToggle(
                      'all',
                      anyActive,
                      anyActive ? 'Todos os fluxos pausados via chat' : 'Todos os fluxos retomados via chat'
                    )
                  }
                  className={`w-full rounded-xl py-2 px-4 text-sm font-medium transition-colors ${
                    isDark
                      ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {anyActive ? 'Pausar Todos os Fluxos' : 'Retomar Todos os Fluxos'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactFlowControl;
